import { createClient } from '@supabase/supabase-js'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_KEY
)

// TENANTS
export async function getTenants() {
  const { data, error } = await supabase
    .from('tenants')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return data
}

export async function createTenant(tenantData) {
  const { data, error } = await supabase
    .from('tenants')
    .insert({
      company_name: tenantData.companyName,
      license_plan: tenantData.licensePlan || 'basic',
      license_status: 'active',
      max_users: tenantData.maxUsers || 3,
      contact_email: tenantData.contactEmail,
      expires_at: tenantData.expiresAt || null,
      notes: tenantData.notes || '',
      business_model: tenantData.businessModel || 'freight_broker',
      // MM-1: modos de transporte solo aplican a ocean_forwarding (mínimo uno).
      enabled_transport_modes: tenantData.businessModel === 'ocean_forwarding'
        ? (tenantData.enabledModes && tenantData.enabledModes.length ? tenantData.enabledModes : ['ocean'])
        : null
    })
    .select()
    .single()
  if (error) throw new Error(error.message)
  return data
}

// MM-1: actualizar los modos de transporte habilitados de un tenant ocean.
export async function updateTenantModes(tenantId, modes) {
  const { error } = await supabase
    .from('tenants')
    .update({ enabled_transport_modes: modes })
    .eq('tenant_id', tenantId)
  if (error) throw new Error(error.message)
}

// MM-1: registrar en audit_logs un cambio de modos (desactivar oculta, no borra).
export async function logTenantAudit(tenantId, action, resourceId, details) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    await supabase.from('audit_logs').insert({
      user_email: user?.email || 'superadmin',
      action,
      resource: 'tenant_modes',
      resource_id: resourceId,
      success: true,
      details,
      tenant_id: tenantId
    })
  } catch (e) { console.error('audit', e) }
}

export async function updateTenant(tenantId, updates) {
  const { error } = await supabase
    .from('tenants')
    .update(updates)
    .eq('tenant_id', tenantId)
  if (error) throw new Error(error.message)
}

export async function suspendTenant(tenantId) {
  const { error } = await supabase
    .from('tenants')
    .update({ license_status: 'suspended' })
    .eq('tenant_id', tenantId)
  if (error) throw new Error(error.message)
}

export async function activateTenant(tenantId) {
  const { error } = await supabase
    .from('tenants')
    .update({ license_status: 'active' })
    .eq('tenant_id', tenantId)
  if (error) throw new Error(error.message)
}

// USERS POR TENANT
export async function getUsersByTenant(tenantId) {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('role')
  if (error) throw new Error(error.message)
  return data
}

// STATS POR TENANT
export async function getTenantStats(tenantId) {
  const [loadsRes, carriersRes, shippersRes, usersRes] = await Promise.all([
    supabase.from('loads').select('load_id, biz_status, customer_rate, margin', { count: 'exact' }).eq('tenant_id', tenantId).eq('is_active', true),
    supabase.from('carriers').select('carrier_id', { count: 'exact' }).eq('tenant_id', tenantId).eq('active', true),
    supabase.from('shippers').select('shipper_id', { count: 'exact' }).eq('tenant_id', tenantId).eq('active', true),
    supabase.from('user_profiles').select('email', { count: 'exact' }).eq('tenant_id', tenantId)
  ])

  const loads = loadsRes.data || []
  const totalRevenue = loads.reduce((s, l) => s + (Number(l.customer_rate) || 0), 0)
  const totalMargin = loads.reduce((s, l) => s + (Number(l.margin) || 0), 0)

  return {
    totalLoads: loadsRes.count || 0,
    totalCarriers: carriersRes.count || 0,
    totalShippers: shippersRes.count || 0,
    totalUsers: usersRes.count || 0,
    totalRevenue,
    totalMargin
  }
}

// OCEAN — config de métricas por tenant (OF-7). El super-admin puede leer/escribir
// gracias al escape de super_admins en la política de tenant_metrics_config.
export async function getTenantMetricsConfig(tenantId) {
  const { data, error } = await supabase.from('tenant_metrics_config').select('config').eq('tenant_id', tenantId).maybeSingle()
  if (error) throw error
  return (data && data.config) || {}
}

export async function saveTenantMetricsConfig(tenantId, config) {
  const { error } = await supabase.from('tenant_metrics_config')
    .upsert({ tenant_id: tenantId, config, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id' })
  if (error) throw error
}

// SUPER ADMINS
export async function getSuperAdmins() {
  const { data, error } = await supabase
    .from('super_admins')
    .select('*')
    .order('created_at')
  if (error) throw new Error(error.message)
  return data
}

export async function addSuperAdmin(email, fullName) {
  const { error } = await supabase
    .from('super_admins')
    .insert({ email, full_name: fullName })
  if (error) throw new Error(error.message)
}

export async function addUserToTenant(tenantId, email, role, fullName) {
  // PLAT-1: portal_scope se deriva del rol (client→client, carrier→carrier, resto→app).
  const portalScope = role === 'client' ? 'client' : role === 'carrier' ? 'carrier' : 'app'
  // Verificar que el usuario existe en user_profiles
  const { data: existing } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('email', email)
    .single()

  if (existing) {
    // Actualizar tenant_id, rol y scope de portal
    const { error } = await supabase
      .from('user_profiles')
      .update({ tenant_id: tenantId, role: role, portal_scope: portalScope })
      .eq('email', email)
    if (error) throw new Error(error.message)
  } else {
    // Crear nuevo perfil
    const { error } = await supabase
      .from('user_profiles')
      .insert({
        email,
        role: role || 'agent',
        full_name: fullName || '',
        tenant_id: tenantId,
        portal_scope: portalScope
      })
    if (error) throw new Error(error.message)
  }
}

export async function createUserInTenant(tenantId, email, password, role, fullName) {
  const response = await fetch('https://dyqfpwuvuxodaesdaqxe.supabase.co/functions/v1/create-user', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-key': 'pyro-2026-secure-admin-key'
    },
    body: JSON.stringify({ email, password, tenantId, role, fullName })
  })

  const data = await response.json()
  if (data.error) throw new Error(data.error)
  return data
}

export async function getAuditLogs(tenantId) {
  const supabase = createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_KEY
  )
  let query = supabase
    .from('audit_logs')
    .select('*')
    .order('timestamp', { ascending: false })
    .limit(100)

  if (tenantId) {
    query = query.eq('tenant_id', tenantId)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)
  return data
}

export async function sendSecurityAlert(event, userEmail, details) {
  try {
    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/security-alert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, userEmail, details })
    })
  } catch (e) {
    console.error('Security alert error:', e)
  }
}